/**
 * Iter128 — Main Hub Company Notification Permissions.
 *
 * Lets `main_hub` / `department_admin` toggle which notification events
 * fan out for the whole company. Critical events (security alerts) are
 * always ON and cannot be muted.
 *
 * Backend:
 *   GET  /api/main-hub/notification-permissions
 *   POST /api/main-hub/notification-permissions
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { BellRing, RefreshCw, Save, Lock } from 'lucide-react';

export default function MainHubNotificationPermissions() {
  const token = localStorage.getItem('nexus_token');
  const H = { Authorization: `Bearer ${token}` };
  const JH = { ...H, 'Content-Type': 'application/json' };

  const [allEvents, setAllEvents] = useState([]);
  const [disabled, setDisabled] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = companyId ? `?company_id=${encodeURIComponent(companyId)}` : '';
      const r = await fetch(`${BACKEND_URL}/api/main-hub/notification-permissions${qs}`, { headers: H });
      if (r.ok) {
        const d = await r.json();
        setAllEvents(d.all_events || []);
        setDisabled(new Set(d.disabled_events || []));
        if (!companyId && d.company_id) setCompanyId(d.company_id);
      } else {
        toast({ title: 'Load failed', variant: 'destructive' });
      }
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [companyId, token]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/main-hub/notification-permissions`, {
        method: 'POST', headers: JH,
        body: JSON.stringify({
          company_id: companyId || undefined,
          disabled_events: [...disabled],
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast({ title: 'Save failed', description: err.detail || '', variant: 'destructive' });
      } else {
        toast({ title: 'Notification permissions saved', description: `${disabled.size} event(s) muted for the company.` });
      }
    } finally { setSaving(false); }
  };

  const toggle = (evt, on) => {
    setDisabled(prev => {
      const next = new Set(prev);
      if (on) next.delete(evt); else next.add(evt);
      return next;
    });
  };

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q ? allEvents.filter(e => e.event.toLowerCase().includes(q) || (e.module || '').toLowerCase().includes(q)) : allEvents;
    const byModule = {};
    for (const e of filtered) {
      const m = e.module || 'system';
      if (!byModule[m]) byModule[m] = [];
      byModule[m].push(e);
    }
    return byModule;
  }, [allEvents, search]);

  return (
    <div className="space-y-6" data-testid="mainhub-notif-perms-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
            <BellRing className="w-6 h-6 text-amber-300" />
          </div>
          <div>
            <div className="text-xs text-amber-300 uppercase tracking-wider font-mono">Main Hub · Notifications</div>
            <h1 className="text-2xl font-bold text-white">Company Notification Permissions</h1>
            <p className="text-slate-400 text-sm">Turn any notification event ON/OFF for the whole company. Critical security alerts cannot be muted.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={load} className="text-slate-300" data-testid="notif-perm-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold" data-testid="notif-perm-save">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader><CardTitle className="text-sm text-white">Company Scope</CardTitle></CardHeader>
        <CardContent className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-xs text-slate-400">Company ID (leave blank for your own company)</label>
            <Input value={companyId} onChange={e => setCompanyId(e.target.value)} placeholder="COMP-XXXXXXXX"
              className="bg-slate-800 border-slate-600 text-white w-72" data-testid="notif-perm-company-id" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs text-slate-400">Search events / modules</label>
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="e.g. training, invoice, message"
              className="bg-slate-800 border-slate-600 text-white" data-testid="notif-perm-search" />
          </div>
          <div className="text-xs text-slate-400 font-mono">
            {allEvents.length} total · <span className="text-red-400">{disabled.size} muted</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(grouped).sort(([a],[b]) => a.localeCompare(b)).map(([mod, list]) => (
          <Card key={mod} className="bg-[#131A2A] border-[#334155]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white uppercase tracking-wider font-mono text-cyan-300">
                {mod} <span className="text-slate-500 font-sans normal-case text-xs">({list.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {list.map(e => {
                const enabled = !disabled.has(e.event);
                return (
                  <div key={e.event}
                    className="flex items-center justify-between text-sm py-2 border-b border-slate-800 last:border-0"
                    data-testid={`notif-perm-${e.event}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-slate-100 font-mono text-xs">{e.event}</div>
                      {e.critical && (
                        <div className="text-[10px] text-red-400 mt-0.5 flex items-center gap-1">
                          <Lock className="w-3 h-3" /> critical — cannot be muted
                        </div>
                      )}
                    </div>
                    <Switch
                      checked={enabled}
                      onCheckedChange={v => toggle(e.event, v)}
                      disabled={e.critical}
                      data-testid={`notif-perm-toggle-${e.event}`}
                    />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
